import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { HelpToggle } from '@/components/common/HelpToggle';
import { guidesCollapsed } from '@/lib/guideDisplay';

interface FieldGuideItem {
  term: string;
  body: string;
}

/**
 * What each number in one section actually counts, stated beside it.
 *
 * The sibling of `ExplainerCard`, and deliberately a different component rather
 * than a prop on it. `ExplainerCard` is page-level, collapsed, and answers "what
 * is this screen"; this is section-level, ALWAYS VISIBLE, and answers "what is
 * this number". The visibility is the whole point: a definition a reader has to
 * open is a definition they read once, and the figures on these screens are
 * exactly the kind that look self-explanatory and are not. "Cases" on the CADE
 * list means something different from "cases" on the activity screen, and the
 * only defence is saying so where the number is.
 *
 * It also avoids a trap this repo has already paid for: prose inside a collapsed
 * `<details>` is present in the DOM but permanently hidden, so a text assertion
 * resolves to it and then waits out its timeout against an element no reader can
 * see. These stay in the flow.
 *
 * Definitions carry NO live values, following `ExplainerCard`. Every agency
 * corpus is rebuilt by ETL runs that truncate and reload — CADE's case table went
 * from 464 rows to 8,123 in a day — so a count written into a locale file is true
 * until the next reload and misleading afterwards, with nothing to signal the
 * change. Live numbers come from the API and render beside the figure. What is
 * stated here is structural: what a column counts, which population it is drawn
 * from, and what it must not be compared with.
 */
export function FieldGuide({
  i18nKey,
  level = 3,
  inline = false,
}: {
  i18nKey: string;
  /** Always open, whatever the page. The method page collects every guide this way. */
  inline?: boolean;
  /**
   * Heading level, 2 or 3. Defaults to 3 because a guide normally sits inside a
   * panel whose own `h2` precedes it. Pass 2 where it follows the page `h1`
   * directly — an unlabelled stats grid is the usual case — because a jump from
   * h1 to h3 is a real `heading-order` violation and a screen-reader user
   * navigating by heading loses a level with no way to tell why. Styling is
   * identical either way: this changes the semantics, not the look.
   */
  level?: 2 | 3;
}) {
  // No namespace list: keys are passed fully qualified (`cade:...`), so
  // restricting namespaces here would only create a second place to keep in step.
  const { t } = useTranslation();

  const items = t(`${i18nKey}.items`, { returnObjects: true, defaultValue: [] });
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const Heading = level === 2 ? 'h2' : 'h3';

  // Behind a help icon where the page asks for it (see `lib/guideDisplay.ts`).
  // The same title, definitions and heading level; only the visibility differs.
  if (!inline && guidesCollapsed()) {
    return (
      <HelpToggle title={t(`${i18nKey}.title`)}>
        <Heading className="text-xs font-medium text-foreground">{t(`${i18nKey}.title`)}</Heading>
        <dl className="space-y-1.5 text-xs">
          {(items as FieldGuideItem[]).map((item) => (
            <div key={item.term} className="sm:flex sm:gap-2">
              <dt className="font-medium text-foreground sm:w-44 sm:shrink-0">{item.term}</dt>
              <dd className="text-muted-foreground">{item.body}</dd>
            </div>
          ))}
        </dl>
      </HelpToggle>
    );
  }

  return (
    <section className="rounded-md border border-dashed bg-muted/30 p-3">
      <Heading className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground">
        <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        {t(`${i18nKey}.title`)}
      </Heading>
      <dl className="space-y-1.5 text-xs">
        {(items as FieldGuideItem[]).map((item) => (
          <div key={item.term} className="sm:flex sm:gap-2">
            <dt className="font-medium text-foreground sm:w-44 sm:shrink-0">{item.term}</dt>
            <dd className="text-muted-foreground">{item.body}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
